import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API, authedFetch, formatApiErrorDetail } from "../lib/api";
import { ArrowLeft, Plus, Trash2, X, FileDown, Pencil, QrCode, ArrowDownToLine, ArrowUpToLine, Wrench } from "lucide-react";
import { StatusBadge, formatDate, daysUntil } from "../components/dpi";
import DamageEntriesTable from "../components/DamageEntriesTable";
import DocumentUploader from "../components/DocumentUploader";
import ManualsView from "../components/ManualsView";
import ScaffoldDialog from "../components/ScaffoldDialog";
import QrOverlay from "../components/QrOverlay";
import PdfDownloadOverlay from "../components/PdfDownloadOverlay";
import RichTextEditor, { RichTextView } from "../components/RichTextEditor";
import ConclusioniPhrasePicker from "../components/ConclusioniPhrasePicker";
import RevisionStartPicker from "../components/RevisionStartPicker";
import RepairDamagesDialog, { damageEntrySignature } from "../components/RepairDamagesDialog";
import { useAuth } from "../context/AuthContext";

function RevisionDialog({ open, scaffoldId, initial, prefill, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const draftKey = `uc_revision_draft_${initial ? `edit_${initial.id}` : `new_${scaffoldId}`}`;
  const blank = {
    revision_date: today,
    expiration_months: 12,
    notes: "",
    criticita: "",
    conclusioni: "",
    signature: "",
    damage_entries: [],
    area_notes: {},
    area_photos: {},
    area_meta: {},
  };
  const [form, setForm] = useState(blank);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [phrasePickerOpen, setPhrasePickerOpen] = useState(false);
  const conclusioniRef = useRef(null);
  const dialogScrollRef = useRef(null);
  const formBottomRef = useRef(null);
  const formTopRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setSavedAt(null);
    setDraftRestored(false);
    // 1) try to restore a saved draft
    let draft = null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) draft = JSON.parse(raw);
    } catch {
      /* ignore */
    }
    if (draft && draft.form) {
      setForm({ ...blank, ...draft.form });
      setDraftRestored(true);
    } else if (initial) {
      setForm({
        revision_date: initial.revision_date,
        expiration_months: initial.expiration_months,
        notes: initial.notes || "",
        criticita: initial.criticita || "",
        conclusioni: initial.conclusioni || "",
        signature: initial.signature || "",
        damage_entries: (initial.damage_entries || []).map((e) => ({ ...e })),
        area_notes: { ...(initial.area_notes || {}) },
        area_photos: Object.fromEntries(
          Object.entries(initial.area_photos || {}).map(([k, v]) => [k, [...(v || [])]])
        ),
        area_meta: JSON.parse(JSON.stringify(initial.area_meta || {})),
      });
    } else if (prefill) {
      // Clone source: a previous revision (passed by ScaffoldDetail). Copy the
      // verification "structure" (notes, criticità, conclusioni, damage rows
      // with their colours/positions/note labels, area notes) AND keep the
      // photo references as-is: photos are stored by path/URL so re-using the
      // same reference does NOT duplicate the underlying file on Object
      // Storage. The user can then keep / replace / remove each photo per row.
      // Only the signature is reset (fresh certification each verifica) and
      // the date / validità default to today + 12 mesi.
      setForm({
        revision_date: today,
        expiration_months: 12,
        notes: prefill.notes || "",
        criticita: prefill.criticita || "",
        conclusioni: prefill.conclusioni || "",
        signature: "",
        damage_entries: (prefill.damage_entries || []).map((e) => ({ ...e })),
        area_notes: { ...(prefill.area_notes || {}) },
        area_photos: Object.fromEntries(
          Object.entries(prefill.area_photos || {}).map(([k, v]) => [k, [...(v || [])]])
        ),
        area_meta: JSON.parse(JSON.stringify(prefill.area_meta || {})),
      });
    } else {
      setForm(blank);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, prefill]);

  // Auto-save: persist current form to localStorage, debounced
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({ form, savedAt: new Date().toISOString() }),
        );
        setSavedAt(new Date());
      } catch {
        /* ignore quota / privacy mode */
      }
    }, 600);
    return () => clearTimeout(t);
  }, [form, open, draftKey]);

  if (!open) return null;

  const isEdit = Boolean(initial);

  const discardDraft = () => {
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setDraftRestored(false);
    setSavedAt(null);
    if (isEdit && initial) {
      setForm({
        revision_date: initial.revision_date,
        expiration_months: initial.expiration_months,
        notes: initial.notes || "",
        criticita: initial.criticita || "",
        conclusioni: initial.conclusioni || "",
        signature: initial.signature || "",
        damage_entries: (initial.damage_entries || []).map((e) => ({ ...e })),
        area_notes: { ...(initial.area_notes || {}) },
        area_photos: Object.fromEntries(
          Object.entries(initial.area_photos || {}).map(([k, v]) => [k, [...(v || [])]])
        ),
        area_meta: JSON.parse(JSON.stringify(initial.area_meta || {})),
      });
    } else {
      setForm(blank);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        ...form,
        expiration_months: Number(form.expiration_months),
      };
      let data;
      if (isEdit) {
        ({ data } = await api.patch(`/scaffold-revisions/${initial.id}`, payload));
      } else {
        ({ data } = await api.post(`/scaffolds/${scaffoldId}/revisions`, payload));
      }
      // Clear draft on successful save
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      setLoading(false);
      onSaved(data);
    } catch (err) {
      setLoading(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const fmtTime = (d) => d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const scrollToBottom = () => {
    formBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };
  const scrollToTop = () => {
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div ref={dialogScrollRef} className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto" onClick={onClose} data-testid="revision-dialog">
      <div className="bru-card w-full max-w-5xl p-6 mt-4 relative" onClick={(e) => e.stopPropagation()}>
        <div ref={formTopRef} />
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="tag-label">{isEdit ? "MODIFICA" : "REGISTRA"} · VERIFICA</div>
            <h3 className="text-2xl font-bold" >
              {isEdit ? "Modifica Verifica" : "Nuova Verifica"}
            </h3>
            {savedAt && (
              <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-1.5" data-testid="autosave-status">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                Bozza salvata in locale alle {fmtTime(savedAt)}
              </div>
            )}
          </div>
          <button onClick={onClose} className="bru-btn bru-btn-ghost !px-2 !py-1" data-testid="close-revision-dialog">
            <X className="w-5 h-5" />
          </button>
        </div>
        {draftRestored && (
          <div className="mb-4 rounded-2xl border border-[#ff5a00]/30 bg-[#fff7f0] px-4 py-3 flex items-center justify-between gap-3" data-testid="draft-restored-banner">
            <div className="text-sm">
              <b>Bozza ripristinata</b> — i dati che avevi compilato sono stati recuperati. Continua da dove avevi lasciato.
            </div>
            <button
              type="button"
              onClick={discardDraft}
              className="bru-btn bru-btn-secondary !px-3 !py-1.5 !text-xs shrink-0"
              data-testid="discard-draft-button"
            >
              Scarta bozza
            </button>
          </div>
        )}
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="bru-label">Data Verifica *</label>
              <input
                type="date"
                required
                min="1990-01-01"
                max="2099-12-31"
                value={form.revision_date}
                onChange={(e) => setForm({ ...form, revision_date: e.target.value })}
                className="bru-input"
                data-testid="revision-date-input"
              />
            </div>
            <div>
              <label className="bru-label">Validità *</label>
              <select
                value={form.expiration_months}
                onChange={(e) => setForm({ ...form, expiration_months: e.target.value })}
                className="bru-input"
                data-testid="revision-months-select"
              >
                <option value={1}>1 mese</option>
                <option value={6}>6 mesi</option>
                <option value={12}>12 mesi</option>
                <option value={24}>24 mesi</option>
              </select>
            </div>
          </div>

          <div>
            <label className="bru-label">Note Generali</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bru-input"
              placeholder="Note generali sulla verifica…"
              data-testid="revision-notes-input"
            />
          </div>

          <DamageEntriesTable
            value={form.damage_entries}
            onChange={(rows) => setForm((prev) => ({ ...prev, damage_entries: rows }))}
            areaNotes={form.area_notes}
            onAreaNotesChange={(an) => setForm((prev) => ({ ...prev, area_notes: an }))}
            areaPhotos={form.area_photos}
            onAreaPhotosChange={(ap) => setForm((prev) => ({ ...prev, area_photos: ap }))}
            areaMeta={form.area_meta}
            onAreaMetaChange={(am) => setForm((prev) => ({ ...prev, area_meta: am }))}
          />

          <div>
            <label className="bru-label">Criticità</label>
            <RichTextEditor
              value={form.criticita}
              onChange={(html) => setForm({ ...form, criticita: html })}
              placeholder="Criticità riscontrate sulla scaffalatura…"
              rows={3}
              data-testid="revision-criticita-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="bru-label !mb-0">Conclusioni</label>
              <button
                type="button"
                className="bru-btn bru-btn-secondary !py-1 !px-2.5 !text-xs"
                onMouseDown={() => conclusioniRef.current?.saveSelection()}
                onClick={() => setPhrasePickerOpen(true)}
                data-testid="open-phrase-picker"
              >
                + Inserisci frase predefinita
              </button>
            </div>
            <RichTextEditor
              ref={conclusioniRef}
              value={form.conclusioni}
              onChange={(html) => setForm({ ...form, conclusioni: html })}
              placeholder="Conclusioni del consulente qualificato…"
              rows={3}
              data-testid="revision-conclusioni-input"
            />
          </div>

          <div>
            <label className="bru-label">Firma consulente</label>
            <input
              value={form.signature}
              onChange={(e) => setForm({ ...form, signature: e.target.value })}
              className="bru-input"
              style={{ fontFamily: "'Brush Script MT', cursive", fontSize: "20px" }}
              placeholder="Firma (testo)"
              data-testid="revision-signature-input"
            />
            <div className="text-[11px] text-neutral-500 mt-1 font-bold uppercase tracking-wider">
              Apparirà in PDF seguito da: CONSULENTE QUALIFICATO AISEM CQ 15635
            </div>
          </div>

          {error && (
            <div className="border-[1.5px] border-black bg-[#ff3b30] text-white px-3 py-2 text-sm" data-testid="revision-error">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="bru-btn bru-btn-secondary" data-testid="cancel-revision-button">
              Annulla
            </button>
            <button type="submit" disabled={loading} className="bru-btn bru-btn-primary disabled:opacity-60" data-testid="submit-revision-button">
              {loading ? "SALVATAGGIO…" : isEdit ? "AGGIORNA VERIFICA" : "SALVA VERIFICA"}
            </button>
          </div>
          <div ref={formBottomRef} />
        </form>
      </div>
      {/* Scroll FABs (only show on larger forms — once damage entries exist) */}
      {(form.damage_entries || []).length > 0 && (
        <div className="fixed right-4 bottom-4 sm:right-6 sm:bottom-6 z-[55] flex flex-col gap-2" data-testid="scroll-fabs">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); scrollToTop(); }}
            className="w-11 h-11 rounded-full bg-white border-[1.5px] border-black shadow-lg flex items-center justify-center hover:bg-neutral-50 active:translate-y-0.5"
            title="Vai in alto"
            data-testid="scroll-to-top-fab"
          >
            <ArrowUpToLine className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); scrollToBottom(); }}
            className="w-11 h-11 rounded-full bg-[#ff5a00] text-white border-[1.5px] border-black shadow-lg flex items-center justify-center hover:bg-[#ff7a30] active:translate-y-0.5"
            title="Vai in fondo"
            data-testid="scroll-to-bottom-fab"
          >
            <ArrowDownToLine className="w-5 h-5" />
          </button>
        </div>
      )}
      <ConclusioniPhrasePicker
        open={phrasePickerOpen}
        onClose={() => setPhrasePickerOpen(false)}
        onInsert={(html) => {
          conclusioniRef.current?.insertHTMLAtSavedCursor(html);
          setPhrasePickerOpen(false);
        }}
      />
    </div>
  );
}

export default function ScaffoldDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [scaffold, setScaffold] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefillSource, setPrefillSource] = useState(null);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [confirmDelRev, setConfirmDelRev] = useState(null);
  const [editScaffoldOpen, setEditScaffoldOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);
  const [repairs, setRepairs] = useState([]);
  const [repairedSigs, setRepairedSigs] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, rRes, repRes] = await Promise.all([
        api.get(`/scaffolds/${id}`),
        api.get(`/scaffolds/${id}/revisions`),
        api.get(`/scaffolds/${id}/repairs`).catch(() => ({ data: [] })),
      ]);
      setScaffold(sRes.data);
      setRevisions(rRes.data);
      setRepairs(repRes.data || []);
      setRepairedSigs(new Set((repRes.data || []).map((r) => r.entry_signature)));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [id]);

  const del = async () => {
    await api.delete(`/scaffolds/${id}`);
    navigate("/scaffolds");
  };

  const deleteRev = async () => {
    if (!confirmDelRev) return;
    await api.delete(`/scaffold-revisions/${confirmDelRev.id}`);
    setConfirmDelRev(null);
    load();
  };

  const downloadPdf = async (revisionId, reference, date) => {
    setPdfDownloading(true);
    try {
      const res = await authedFetch(`${API}/scaffold-revisions/${revisionId}/pdf`);
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        alert(`Errore nella generazione del PDF (HTTP ${res.status}). ${txt.slice(0, 200)}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verifica_${(reference || "scaffalatura").replace(/\s+/g, "_")}_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert(`Errore di rete durante il download PDF: ${e.message || e}`);
    } finally {
      setPdfDownloading(false);
    }
  };

  const downloadQr = () => setQrOpen(true);

  const downloadRepairsReport = async () => {
    setPdfDownloading(true);
    try {
      const res = await authedFetch(`${API}/scaffolds/${id}/repairs/pdf`);
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        alert(`Errore nella generazione del PDF (HTTP ${res.status}). ${txt.slice(0, 200)}`);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      let filename = `ripristini_${(scaffold?.reference || "scaffalatura").replace(/\s+/g, "_")}.pdf`;
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert(`Errore di rete: ${e.message || e}`);
    } finally {
      setPdfDownloading(false);
    }
  };

  // Handler for "Nuova Verifica": if there are prior revisions AND no pending
  // draft in localStorage, show the start-mode picker (Nuova da zero / Usa
  // precedente). Otherwise go straight to the empty dialog.
  const hasPendingNewDraft = () => {
    try { return !!localStorage.getItem(`uc_revision_draft_new_${id}`); }
    catch { return false; }
  };
  const onNewRevisionClick = () => {
    setEditing(null);
    setPrefillSource(null);
    if (revisions.length > 0 && !hasPendingNewDraft()) {
      setStartPickerOpen(true);
    } else {
      setDialogOpen(true);
    }
  };

  if (loading || !scaffold) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="tag-label">CARICAMENTO…</div>
      </div>
    );
  }

  const exp = scaffold.latest_revision?.expiration_date;
  const du = daysUntil(exp);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="scaffold-detail-page">
      <Link to="/scaffolds" className="inline-flex items-center gap-2 mb-6 bru-link text-sm" data-testid="back-to-scaffolds-link">
        <ArrowLeft className="w-4 h-4" />
        Torna alle Scaffalature
      </Link>

      <div className="bru-card p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="bru-badge bg-white">SCAFFALATURA</span>
              <StatusBadge status={scaffold.status} />
            </div>
            <div className="tag-label mb-1">RIFERIMENTO</div>
            <h1 className="text-4xl font-bold mb-2 tracking-tight" >
              {scaffold.reference}
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
              <div>
                <div className="tag-label">Cliente</div>
                <div className="font-bold">{scaffold.client_name}</div>
              </div>
              <div className="md:col-span-2">
                <div className="tag-label">Indirizzo</div>
                <div className="font-bold">{scaffold.address}</div>
              </div>
              <div>
                <div className="tag-label">Prossima Scadenza</div>
                <div className="font-bold">{formatDate(exp)}</div>
                {du !== null && (
                  <div className={`text-xs ${du < 0 ? "text-[#ff3b30]" : du <= 30 ? "text-[#a36b00]" : "text-neutral-500"}`}>
                    {du < 0 ? `Scaduta da ${Math.abs(du)} gg` : `${du} gg rimanenti`}
                  </div>
                )}
              </div>
              <div>
                <div className="tag-label">Verifiche</div>
                <div className="font-bold">{scaffold.revisions_count}</div>
              </div>
            </div>
            {scaffold.notes && (
              <div className="mt-5 pt-5 border-t-[1.5px] border-neutral-200">
                <div className="tag-label mb-1">Note</div>
                <div className="text-sm whitespace-pre-line">{scaffold.notes}</div>
              </div>
            )}
            {(scaffold.planimetria || []).length > 0 || isAdmin ? (
              <div className="mt-5 pt-5 border-t-[1.5px] border-neutral-200">
                <div className="tag-label mb-2">Planimetria</div>
                {(scaffold.planimetria || []).length === 0 ? (
                  <div className="text-xs text-neutral-500">
                    Nessuna planimetria caricata. Usa il pulsante <b>Modifica Anagrafica</b> per aggiungerle.
                  </div>
                ) : (
                  <DocumentUploader value={scaffold.planimetria || []} onChange={() => {}} readOnly />
                )}
              </div>
            ) : null}
            {((scaffold.documents || []).length > 0 || (scaffold.manual_ids || []).length > 0) && (
              <div className="mt-5 pt-5 border-t-[1.5px] border-neutral-200">
                <div className="tag-label mb-2">Documenti</div>
                {(scaffold.documents || []).length > 0 && (
                  <div className="mb-2">
                    <DocumentUploader value={scaffold.documents || []} onChange={() => {}} readOnly />
                  </div>
                )}
                <ManualsView kind="scaffold" manualIds={scaffold.manual_ids || []} />
              </div>
            )}
          </div>
          {isAdmin && (
            <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
              <button onClick={onNewRevisionClick} className="bru-btn bru-btn-primary" data-testid="add-revision-button">
                <Plus className="w-4 h-4" />
                Nuova Verifica
              </button>
              {(revisions[0]?.damage_entries || []).length > 0 && (
                <button
                  onClick={() => setRepairDialogOpen(true)}
                  className="bru-btn bru-btn-secondary"
                  data-testid="open-repair-dialog"
                >
                  <Wrench className="w-4 h-4" />
                  Sistema Danni
                  {repairs.length > 0 && (
                    <span className="ml-1 bg-[#10B981] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                      {repairs.length}
                    </span>
                  )}
                </button>
              )}
              <button onClick={() => setEditScaffoldOpen(true)} className="bru-btn bru-btn-secondary" data-testid="edit-scaffold-button">
                <Pencil className="w-4 h-4" />
                Modifica Anagrafica
              </button>
              <button onClick={downloadQr} className="bru-btn bru-btn-secondary" data-testid="download-qr-scaffold">
                <QrCode className="w-4 h-4" />
                QR Code
              </button>
              <button onClick={() => setConfirm(true)} className="bru-btn bru-btn-secondary !text-[#ff3b30]" data-testid="delete-scaffold-button">
                <Trash2 className="w-4 h-4" />
                Elimina Scaffalatura
              </button>
            </div>
          )}
          {!isAdmin && (
            <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
              <button onClick={downloadQr} className="bru-btn bru-btn-secondary" data-testid="download-qr-scaffold">
                <QrCode className="w-4 h-4" />
                QR Code
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="tag-label mb-1">STORICO · VERIFICHE</div>
          <h2 className="text-3xl font-bold" >Verifiche registrate</h2>
        </div>
        <div className="tag-label">{revisions.length} verific{revisions.length === 1 ? "a" : "he"}</div>
      </div>

      {revisions.length === 0 ? (
        <div className="bru-card p-10 text-center" data-testid="revisions-empty">
          <div className="tag-label mb-2">NESSUNA VERIFICA</div>
          <h3 className="text-xl font-bold mb-2">Questa scaffalatura non è stata ancora verificata</h3>
          {isAdmin && (
            <button onClick={onNewRevisionClick} className="bru-btn bru-btn-primary mt-3" data-testid="empty-add-revision-button">
              <Plus className="w-4 h-4" />
              Prima Verifica
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="revisions-list">
          {revisions.map((r, idx) => {
            const isLatest = idx === 0;
            return (
            <div
              key={r.id}
              className={`bru-card ${isLatest ? "p-6 ring-1 ring-[#ff5a00]/20" : "p-4"}`}
              data-testid={`revision-row-${r.id}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-5 items-start">
                <div className="flex md:flex-col items-center md:items-start gap-4 md:min-w-[160px]">
                  {isLatest ? (
                    <div className="bg-neutral-900 text-white px-5 py-4 rounded-2xl">
                      <div className="text-[10px] uppercase tracking-[0.15em] opacity-60">Ultima Verifica</div>
                      <div className="font-bold text-3xl leading-none mt-1.5">{formatDate(r.revision_date)}</div>
                    </div>
                  ) : (
                    <div className="bg-neutral-100 text-neutral-700 px-3 py-2 rounded-xl">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">Verifica</div>
                      <div className="font-semibold text-base leading-none mt-1">{formatDate(r.revision_date)}</div>
                    </div>
                  )}
                </div>
                <div className={`${isLatest ? "space-y-3" : "space-y-2"} min-w-0`}>
                  <div className="flex gap-2 flex-wrap">
                    <span className="bru-badge bg-white">
                      VALIDITÀ {r.expiration_months} {r.expiration_months === 1 ? "MESE" : "MESI"}
                    </span>
                    <span className="bru-badge bg-[#f5f5f5]">SCADE {formatDate(r.expiration_date)}</span>
                    <span className="bru-badge bg-white">{(r.damage_entries || []).length} righe</span>
                  </div>
                  {isLatest && r.notes && (
                    <div>
                      <div className="tag-label mb-1">Note</div>
                      <div className="text-sm whitespace-pre-line">{r.notes}</div>
                    </div>
                  )}
                  {isLatest && (r.damage_entries || []).length > 0 && (
                    <DamageEntriesTable
                      value={r.damage_entries}
                      onChange={() => {}}
                      areaNotes={r.area_notes || {}}
                      areaPhotos={r.area_photos || {}}
                      areaMeta={r.area_meta || {}}
                      readOnly
                    />
                  )}
                  {isLatest && r.criticita && (
                    <div>
                      <div className="tag-label mb-1">Criticità</div>
                      <RichTextView html={r.criticita} data-testid={`criticita-view-${r.id}`} />
                    </div>
                  )}
                  {isLatest && r.conclusioni && (
                    <div>
                      <div className="tag-label mb-1">Conclusioni</div>
                      <RichTextView html={r.conclusioni} data-testid={`conclusioni-view-${r.id}`} />
                    </div>
                  )}
                  {r.signature && (
                    <div>
                      <div className="tag-label mb-1">Firma</div>
                      <div className="font-bold italic" style={{ fontFamily: "'Brush Script MT', cursive", fontSize: isLatest ? "20px" : "16px" }}>
                        {r.signature}
                      </div>
                      {isLatest && (
                        <div className="text-[11px] text-neutral-500 mt-0.5 font-bold uppercase tracking-wider">
                          Consulente Qualificato AISEM CQ 15635
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => downloadPdf(r.id, scaffold.reference, r.revision_date)}
                    className={`bru-btn bru-btn-secondary ${!isLatest ? "!px-3 !py-1.5 !text-xs" : ""}`}
                    data-testid={`download-pdf-${r.id}`}
                  >
                    <FileDown className="w-4 h-4" />
                    Scarica Certificato
                  </button>
                  {isLatest && repairs.length > 0 && (
                    <button
                      onClick={downloadRepairsReport}
                      className="bru-btn bru-btn-secondary"
                      data-testid={`download-repairs-report-${r.id}`}
                    >
                      <FileDown className="w-4 h-4" />
                      Report Ripristini ({repairs.length})
                    </button>
                  )}
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => { setEditing(r); setDialogOpen(true); }}
                        className={`bru-btn bru-btn-secondary ${!isLatest ? "!px-3 !py-1.5 !text-xs" : ""}`}
                        data-testid={`edit-revision-${r.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                        Modifica
                      </button>
                      <button
                        onClick={() => setConfirmDelRev(r)}
                        className={`bru-btn bru-btn-secondary !text-[#ff3b30] ${!isLatest ? "!px-3 !py-1.5 !text-xs" : ""}`}
                        data-testid={`delete-revision-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Elimina
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {isAdmin && (
        <RevisionStartPicker
          open={startPickerOpen}
          lastRevision={revisions[0] || null}
          onClose={() => setStartPickerOpen(false)}
          onPickBlank={() => {
            setStartPickerOpen(false);
            setPrefillSource(null);
            setDialogOpen(true);
          }}
          onPickClone={async () => {
            setStartPickerOpen(false);
            const src = revisions[0];
            if (!src) return;
            // Filter out damage entries already marked as repaired so they
            // don't reappear in the new verifica. Signatures are computed
            // client-side and mirror the backend hash.
            const filteredEntries = [];
            for (const e of src.damage_entries || []) {
              const sig = await damageEntrySignature(e);
              if (!repairedSigs.has(sig)) filteredEntries.push(e);
            }
            setPrefillSource({ ...src, damage_entries: filteredEntries });
            setDialogOpen(true);
          }}
        />
      )}

      {isAdmin && (
        <RevisionDialog
          open={dialogOpen}
          scaffoldId={id}
          initial={editing}
          prefill={prefillSource}
          onClose={() => { setDialogOpen(false); setEditing(null); setPrefillSource(null); }}
          onSaved={() => {
            setDialogOpen(false);
            setEditing(null);
            setPrefillSource(null);
            load();
          }}
        />
      )}

      {isAdmin && (
        <RepairDamagesDialog
          open={repairDialogOpen}
          scaffold={scaffold}
          latestRevision={revisions[0] || null}
          onClose={() => setRepairDialogOpen(false)}
          onChanged={() => { load(); }}
        />
      )}

      {isAdmin && (
        <ScaffoldDialog
          open={editScaffoldOpen}
          initial={scaffold}
          onClose={() => setEditScaffoldOpen(false)}
          onSaved={() => {
            setEditScaffoldOpen(false);
            load();
          }}
        />
      )}

      <QrOverlay
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        kind="scaffold"
        id={id}
        title={scaffold?.reference || "Scaffalatura"}
        filename={`qr_${scaffold?.reference || "scaffalatura"}`}
      />

      <PdfDownloadOverlay
        open={pdfDownloading}
        title="Generazione certificato in corso…"
        subtitle="Stiamo preparando il PDF della verifica."
      />

      {confirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-scaffold-dialog">
          <div className="bru-card p-6 max-w-sm w-full">
            <div className="tag-label mb-1">CONFERMA</div>
            <h3 className="text-xl font-bold mb-3" >Eliminare la scaffalatura?</h3>
            <p className="text-sm text-neutral-600 mb-5">
              Verranno eliminate anche le {scaffold.revisions_count} revision{scaffold.revisions_count === 1 ? "e" : "i"}.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirm(false)} className="bru-btn bru-btn-secondary" data-testid="cancel-delete-scaffold-button">Annulla</button>
              <button onClick={del} className="bru-btn bru-btn-danger" data-testid="confirm-delete-scaffold-button">Elimina Tutto</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelRev && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-revision-dialog">
          <div className="bru-card p-6 max-w-sm w-full">
            <div className="tag-label mb-1">CONFERMA</div>
            <h3 className="text-xl font-bold mb-3" >Eliminare questa verifica?</h3>
            <p className="text-sm text-neutral-600 mb-5">
              La verifica del {formatDate(confirmDelRev.revision_date)} verrà rimossa. Operazione non reversibile.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelRev(null)} className="bru-btn bru-btn-secondary" data-testid="cancel-delete-revision-button">Annulla</button>
              <button onClick={deleteRev} className="bru-btn bru-btn-danger" data-testid="confirm-delete-revision-button">Elimina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
